/**
 * Member Designation Service
 * Handles member designation assignment operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MemberDesignationService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberDesignationService.DEFAULT_SERVICE_PATH,
            basePath: MemberDesignationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all member designations with optional filters
     * @param memberId - Optional filter by member ID
     * @param designationId - Optional filter by designation ID
     * @param isActive - Optional filter for active/inactive assignments
     * @returns Array of member designations
     */
    async getAll(memberId, designationId, isActive) {
        const response = await this.getAllRaw(memberId, designationId, isActive);
        return response.data ?? [];
    }
    /**
     * Get all member designations with optional filters (raw response)
     * @param memberId - Optional filter by member ID
     * @param designationId - Optional filter by designation ID
     * @param isActive - Optional filter for active/inactive assignments
     * @returns Raw response with array of member designations
     */
    getAllRaw(memberId, designationId, isActive) {
        const params = {};
        if (memberId !== undefined)
            params.memberId = memberId;
        if (designationId !== undefined)
            params.designationId = designationId;
        if (isActive !== undefined)
            params.isActive = isActive;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE KEY ====================
    /**
     * Get member designation by composite key
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Member designation details
     */
    async getByCompositeKey(memberId, designationId) {
        const response = await this.getByCompositeKeyRaw(memberId, designationId);
        if (!response.data) {
            throw new Error(`Member designation not found for Member ID ${memberId} and Designation ID ${designationId}`);
        }
        return response.data;
    }
    /**
     * Get member designation by composite key (raw response)
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Raw response with member designation details
     */
    getByCompositeKeyRaw(memberId, designationId) {
        const url = this.buildUrl(`${memberId}/${designationId}`);
        return this.http.get(url);
    }
    // ==================== GET BY MEMBER ====================
    /**
     * Get all designations for a member
     * @param memberId - Member ID
     * @returns Array of designations for the member
     */
    async getByMember(memberId) {
        const response = await this.getByMemberRaw(memberId);
        return response.data ?? [];
    }
    /**
     * Get all designations for a member (raw response)
     * @param memberId - Member ID
     * @returns Raw response with array of designations
     */
    getByMemberRaw(memberId) {
        const url = this.buildUrl(`member/${memberId}`);
        return this.http.get(url);
    }
    // ==================== GET BY DESIGNATION ====================
    /**
     * Get all members with a designation
     * @param designationId - Designation ID
     * @returns Array of members with the designation
     */
    async getByDesignation(designationId) {
        const response = await this.getByDesignationRaw(designationId);
        return response.data ?? [];
    }
    /**
     * Get all members with a designation (raw response)
     * @param designationId - Designation ID
     * @returns Raw response with array of members
     */
    getByDesignationRaw(designationId) {
        const url = this.buildUrl(`designation/${designationId}`);
        return this.http.get(url);
    }
    // ==================== MODEL VIEWS ====================
    /**
     * Get ModelView for a member designation by composite key
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Member designation ModelView
     */
    async getMemberDesignationView(memberId, designationId) {
        const response = await this.getMemberDesignationViewRaw(memberId, designationId);
        if (!response.data) {
            throw new Error('Member designation not found');
        }
        return response.data;
    }
    /**
     * Get ModelView for a member designation by composite key (raw response)
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Raw response with member designation ModelView
     */
    getMemberDesignationViewRaw(memberId, designationId) {
        const url = this.buildUrl(`view/${memberId}/${designationId}`);
        return this.http.get(url);
    }
    /**
     * Get ModelViews for all designations assigned to a member
     * @param memberId - Member ID
     * @returns Array of member designation ModelViews
     */
    async getMemberDesignationViewsByMember(memberId) {
        const response = await this.getMemberDesignationViewsByMemberRaw(memberId);
        return response.data ?? [];
    }
    /**
     * Get ModelViews for all designations assigned to a member (raw response)
     * @param memberId - Member ID
     * @returns Raw response with array of member designation ModelViews
     */
    getMemberDesignationViewsByMemberRaw(memberId) {
        const url = this.buildUrl(`member/${memberId}/view`);
        return this.http.get(url);
    }
    /**
     * Get ModelViews for members assigned to a designation
     * @param designationId - Designation ID
     * @returns Array of member designation ModelViews
     */
    async getMemberDesignationViewsByDesignation(designationId) {
        const response = await this.getMemberDesignationViewsByDesignationRaw(designationId);
        return response.data ?? [];
    }
    /**
     * Get ModelViews for members assigned to a designation (raw response)
     * @param designationId - Designation ID
     * @returns Raw response with array of member designation ModelViews
     */
    getMemberDesignationViewsByDesignationRaw(designationId) {
        const url = this.buildUrl(`designation/${designationId}/view`);
        return this.http.get(url);
    }
    /**
     * Get filtered member designation ModelViews with pagination
     * @param filter - Filter criteria including pagination
     * @returns Paged result of member designation ModelViews
     */
    async getMemberDesignationListView(filter) {
        const response = await this.getMemberDesignationListViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve member designation list view');
        }
        return response.data;
    }
    /**
     * Get filtered member designation ModelViews with pagination (raw response)
     * @param filter - Filter criteria including pagination
     * @returns Raw response with paged result
     */
    getMemberDesignationListViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter || {});
    }
    // ==================== CREATE ====================
    /**
     * Create a new member designation assignment
     * @param data - Member designation creation data with MemberId, DesignationId, and IsMain
     * @returns Created or reactivated member designation
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create member designation');
        }
        return response.data;
    }
    /**
     * Create a new member designation assignment (raw response)
     * @param data - Member designation creation data
     * @returns Raw response with created or reactivated member designation
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing member designation (IsMain and IsActive)
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @param data - Update model with IsMain and IsActive
     * @returns Updated member designation
     */
    async update(memberId, designationId, data) {
        const response = await this.updateRaw(memberId, designationId, data);
        if (!response.data) {
            throw new Error(`Failed to update member designation for Member ID ${memberId} and Designation ID ${designationId}`);
        }
        return response.data;
    }
    /**
     * Update an existing member designation (raw response)
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @param data - Update model with IsMain and IsActive
     * @returns Raw response with updated member designation
     */
    updateRaw(memberId, designationId, data) {
        const url = this.buildUrl(`${memberId}/${designationId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a member designation
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Success status
     */
    async delete(memberId, designationId) {
        const response = await this.deleteRaw(memberId, designationId);
        return response.success;
    }
    /**
     * Soft delete a member designation (raw response)
     * @param memberId - Member ID
     * @param designationId - Designation ID
     * @returns Raw response with success status
     */
    deleteRaw(memberId, designationId) {
        const url = this.buildUrl(`${memberId}/${designationId}`);
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate member designation cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate member designation cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MemberDesignationService.DEFAULT_SERVICE_PATH = 'departments';
MemberDesignationService.BASE_PATH = 'api/member-designation';
//# sourceMappingURL=MemberDesignationService.js.map